// All copy for the superadmin Broadcasts page. Arabic is Egyptian, matching
// the rest of the system's Arabic hints (payroll, calendar, settings).
// Pick with `text[language]` — see context.js.

const en = {
  lang: "en",
  // ── page ──
  title: "Broadcasts",
  subtitle: "Announcements shown live to the people you choose — across every client.",
  loading: "Loading broadcasts...",
  error: "Error",
  success: "Done",
  close: "Close",
  cancel: "Cancel",
  back: "Back",
  edit: "Edit",
  delete: "Delete",
  refresh: "Refresh",
  saving: "Saving…",
  working: "Working…",
  none: "None",
  required: "Required",

  tabs: {
    compose: "Compose",
    templates: "Templates",
    audiences: "Audiences",
    history: "History",
  },

  stats: {
    templates: "Active templates",
    audiences: "Saved audiences",
    scheduled: "Scheduled",
    sent: "Sent",
  },

  loadFailed: {
    templates: "Couldn't load templates",
    audiences: "Couldn't load audiences",
    options: "Couldn't load target options",
  },

  // ── labels for enums ──
  categories: {
    VERSION_UPDATE: "Version update",
    SCHEDULED_MAINTENANCE: "Scheduled maintenance",
    UNPLANNED_DOWNTIME: "Unplanned downtime",
    SERVICE_RESTORED: "Service restored",
    DEGRADED_PERFORMANCE: "Degraded performance",
    SECURITY_NOTICE: "Security notice",
    POLICY_UPDATE: "Policy update",
    HOLIDAY_CLOSURE: "Holiday / closure",
    PAYROLL_NOTICE: "Payroll notice",
    NEW_FEATURE: "New feature",
    EMERGENCY: "Emergency",
    GENERAL: "General",
  },
  severities: { INFO: "Info", SUCCESS: "Success", WARNING: "Warning", CRITICAL: "Critical" },
  displayModes: {
    BANNER: { label: "Banner", hint: "Strip at the top of every page" },
    TOAST: { label: "Toast", hint: "Small card in the corner, hides by itself" },
    MODAL: { label: "Pop-up", hint: "Covers the screen until closed or acknowledged" },
  },
  statuses: {
    DRAFT: "Draft",
    SCHEDULED: "Scheduled",
    SENDING: "Sending",
    SENT: "Sent",
    CANCELLED: "Cancelled",
    FAILED: "Failed",
    EXPIRED: "Expired",
  },
  roles: { superadmin: "Super admins", admin: "Admins", manager: "Managers", user: "Employees" },
  ruleTypes: {
    ALL: "Everyone",
    ROLE: "Role",
    CLIENT: "Client",
    WORKING_COMPANY: "Working company",
    DEPARTMENT: "Department",
    ADMIN_LEVEL: "Admin level",
    GROUP: "Group",
    USER: "Specific person",
  },
  variableTypes: { text: "Text", datetime: "Date & time", number: "Number" },

  // ── compose ──
  compose: {
    editing: (id) => `Editing broadcast #${id}. Saving replaces it.`,
    startOver: "Start over",
    stepTemplate: "Start from a template",
    stepTemplateSub: "Pick a ready-made message or one of your presets",
    clear: "Clear",
    yourPresets: "Your presets",
    stepDetails: "Fill in the details",
    stepDetailsSub: "These values fill the {{placeholders}} in both languages",
    detailsHint: "Dates are shown in each reader's own language.",
    stepMessage: "Message",
    stepMessageSub: "What people will read, and how it shows up",
    titleEn: "Title (English)",
    bodyEn: "Message (English)",
    titleAr: "Title (Arabic)",
    bodyAr: "Message (Arabic)",
    arabicVersion: "Arabic version",
    arabicVersionHint: "Shown to people using the system in Arabic. When empty they see the English one.",
    category: "Category",
    severity: "Severity",
    displayAs: "Display as",
    requireAck: "Require acknowledgement",
    requireAckHint: 'Stays until each person clicks "I acknowledge". You can see who did in History.',
    dismissible: "Let people dismiss it",
    dismissibleHint: "When off, it stays until it expires (useful for live downtime notices).",
    addToInbox: "Also add to the notification bell",
    addToInboxHint: "Keeps a copy in each person's notifications list.",
    stepAudience: "Who receives it",
    stepAudienceSub: "Build rules, or pick an audience you saved before",
    buildRules: "Build rules",
    savedAudience: "Saved audience",
    selectAudience: "Select audience…",
    noSavedAudiences: "No saved audiences yet — create them in the Audiences tab.",
    stepDelivery: "Delivery",
    stepDeliverySub: "When it goes out and when it stops showing",
    send: "Send",
    now: "Now",
    schedule: "Schedule",
    date: "Date",
    time: "Time",
    stopsShowing: "Stops showing",
    expiresHint: (when) => `Stops showing ${when}`,
    expiryNoneHint: "Stays until dismissed / acknowledged, or you retract it.",
    expiryNone: "Never (manual)",
    expiryHours: "After a duration",
    expiryAt: "At a specific time",
    hours: "hours",
    errTitleEn: "English title is required",
    errBodyEn: "English message is required",
    errFillIn: (list) => `Fill in: ${list}`,
    errUnknownPlaceholders: (list) => `Unknown placeholders: ${list}`,
    errAudience: "Choose who receives this",
    errFutureTime: "Pick a future send time",
    errExpiry: "The stop time must be after the send time",
    saveAsPreset: "Save as preset",
    saveDraft: "Save draft",
    sendNow: "Send now",
    scheduleBtn: "Schedule",
    preview: "Preview",
    previewSub: "Exactly how people will see it",
    english: "English",
    arabic: "Arabic",
    noArabicPreview: "No Arabic text — people using Arabic will see the English version.",
    recipients: (n) => `${n.toLocaleString()} recipient${n === 1 ? "" : "s"}`,
    sentTo: (n) => `Sent to ${n.toLocaleString()} ${n === 1 ? "person" : "people"}.`,
    scheduledFor: (when) => `Scheduled for ${when}.`,
    draftSaved: "Draft saved.",
    confirmSendTitle: "Send this announcement now?",
    confirmScheduleTitle: "Schedule this announcement?",
    sendToN: (n) => `Send to ${n == null ? "…" : n.toLocaleString()} people`,
    goesOutAt: (when) => `Goes out ${when}`,
    goesOutNow: "Goes out immediately",
    andExpires: (when) => ` · stops showing ${when}`,
    audience: "Audience",
    noArabicWarning: "No Arabic version — people using Arabic will see English.",
    presetName: "Preset name",
    presetNameHint:
      "Saves the text, placeholders, display settings, duration and saved audience (if any). Values you typed for the placeholders are not saved.",
    savePreset: "Save preset",
    presetSaved: (name) => `Preset "${name}" saved.`,
  },

  // ── templates ──
  templates: {
    yourPresets: "Your presets",
    yourPresetsSub: "Your own reusable messages — edit them any time",
    newPreset: "New preset",
    noPresets: 'No presets yet. Create one here, use "Save as preset" while composing, or duplicate a standard template.',
    standard: "Standard templates",
    standardSub: "Built in and read-only. Duplicate one to make your own version.",
    standardBadge: "Standard",
    use: "Use",
    duplicate: "Duplicate as preset",
    duplicateShort: "Duplicate",
    colTemplate: "Template",
    colMessage: "Message",
    colStatus: "Status",
    colActions: "Actions",
    active: "Active",
    off: "Off",
    needsAck: "needs acknowledgement",
    hoursShort: (n) => `${n}h`,
    defaultAudience: (name) => `Default audience: ${name}`,
    duplicated: (name) => `"${name}" was copied into your presets.`,
    deleteTitle: "Delete preset?",
    deleteBody: (name) => `"${name}" will be removed. Broadcasts already sent from it keep their text.`,
    deleted: (name) => `"${name}" deleted.`,
  },

  // ── template editor ──
  editor: {
    editTitle: (name) => `Edit preset — ${name}`,
    newTitle: "New preset",
    basics: "Basics",
    behavior: "Behavior",
    name: "Name",
    nameHint: "Only you see this — it's how you find the preset later.",
    description: "Description (internal)",
    descriptionHint: "Optional note for you, e.g. when to use this preset.",
    content: "Message text",
    contentHint: "Write the announcement once. Put {{name}} anywhere a value changes each time — you fill it in when sending.",
    contentExample: "Example: Version {{version}} is now live",
    placeholders: "Placeholders",
    placeholdersHint: "Every {{…}} in the text needs a row here, so the sender knows what to fill in.",
    add: "Add placeholder",
    usedNotDefined: "Used in the text but not added yet — click to add:",
    noPlaceholders: "No placeholders — the message is sent exactly as written.",
    colKey: "Placeholder",
    colLabelEn: "Label (English)",
    colLabelAr: "Label (Arabic)",
    colType: "Type",
    previewTitle: "Preview",
    previewHint: "How it looks with the placeholders still empty.",
    key: "key",
    labelEn: "Label (English)",
    labelAr: "Label (Arabic)",
    removePlaceholder: "Remove placeholder",
    defaults: "Defaults",
    showForHours: "Show for (hours)",
    showForHint: "Empty = until dismissed or retracted",
    defaultAudience: "Default audience",
    dismissible: "Dismissible",
    savePreset: "Save preset",
  },

  // ── audiences ──
  audiences: {
    title: "Saved audiences",
    subtitle: "Groups you announce to often — pick them in one click while composing",
    newAudience: "New audience",
    empty: "Save the groups you announce to often, then pick them in one click while composing.",
    colName: "Name",
    colWho: "Who",
    colActions: "Actions",
    editTitle: (name) => `Edit audience — ${name}`,
    newTitle: "New audience",
    basics: "Basics",
    name: "Name",
    namePlaceholder: "e.g. All Acme managers",
    description: "Description",
    rules: "Rules",
    rulesHint: "Anyone matching an Include row, minus anyone matching an Exclude row.",
    save: "Save audience",
    saved: (name) => `Audience "${name}" saved.`,
    deleteTitle: "Delete audience?",
    deleteBody: (name) =>
      `"${name}" will be removed. Broadcasts already sent to it are unaffected — each keeps its own copy of the rules.`,
    deleted: (name) => `"${name}" deleted.`,
  },

  // ── reach counter ──
  reach: {
    incomplete: "Complete the rules to see who this reaches.",
    counting: "Counting…",
    reaches: (n) => `Reaches ${n.toLocaleString()} ${n === 1 ? "person" : "people"}`,
    eg: "e.g.",
  },

  // ── rule builder ──
  rules: {
    include: "Include",
    exclude: "Exclude",
    includeOrExclude: "Include or exclude",
    ruleType: "Rule type",
    none: "No rules yet — add who should receive this.",
    addRule: "Add rule",
    addExclusion: "Add exclusion",
    removeRule: "Remove rule",
    everyActive: "Every active account",
    selectRole: "Select role…",
    selectClient: "Select client…",
    client: "Client…",
    company: "Company…",
    selectDepartment: "Select department…",
    selectAdminLevel: "Select admin level…",
    selectGroup: "Select group…",
    searchUser: "Search name, email, phone or client code…",
    noUsers: "No matching people",
    change: "Change",
    user: (id) => `User #${id}`,
    clientRule: (name) => `Client: ${name}`,
    deptRule: (name, company) => `Dept: ${name}${company ? ` (${company})` : ""}`,
    deptN: (id) => `Department #${id}`,
    adminLevelRule: (name) => `Admin level: ${name}`,
    adminLevelN: (id) => `Admin level #${id}`,
    groupRule: (name) => `Group: ${name}`,
    group: "Group",
    except: (label) => `except ${label}`,
    listSep: ", ",
  },

  // ── history ──
  history: {
    title: "Broadcast history",
    subtitle: "Every announcement with its status and delivery stats",
    status: "Status",
    allStatuses: "All statuses",
    empty: "Nothing here yet. Broadcasts you send or schedule appear here.",
    colAnnouncement: "Announcement",
    colStatus: "Status",
    colAudience: "Audience",
    colWhen: "When",
    colDelivery: "Delivery",
    colActions: "Actions",
    fromTemplate: (name) => `from "${name}"`,
    scheduledAt: (when) => `Scheduled ${when}`,
    sentAt: (when) => `Sent ${when}`,
    createdAt: (when) => `Created ${when}`,
    until: (when) => `Until ${when}`,
    by: (who) => `by ${who}`,
    recipientsN: (n) => `${n.toLocaleString()} recipients`,
    seen: "Seen",
    acknowledged: "Acknowledged",
    recipients: "Recipients",
    sendNow: "Send now",
    cancel: "Cancel",
    retract: "Retract",
    confirm: {
      send: { title: "Send now?", body: "It goes out immediately to everyone matching its audience.", btn: "Send now" },
      cancel: { title: "Cancel this broadcast?", body: "It will not be sent.", btn: "Cancel broadcast" },
      retract: {
        title: "Retract this announcement?",
        body: "It disappears from every screen right away. Delivery stats are kept.",
        btn: "Retract",
      },
      delete: { title: "Delete this broadcast?", body: "It is removed from history permanently.", btn: "Delete" },
    },
    done: { send: "Sent.", retract: "Retracted from everyone's screen.", cancel: "Cancelled.", delete: "Deleted." },
    recipientsTitle: (title) => `Recipients — ${title}`,
    filters: { all: "All", unseen: "Not seen", seen: "Seen", acknowledged: "Acknowledged", dismissed: "Dismissed" },
    colPerson: "Person",
    colRole: "Role",
    colClient: "Client",
    colSeen: "Seen",
    colAck: "Acknowledged",
    colDismissed: "Dismissed",
    nobody: "Nobody here.",
    loadingRecipients: "Loading recipients...",
  },
};

const ar = {
  lang: "ar",
  // ── page ──
  title: "الإعلانات",
  subtitle: "إعلانات بتظهر فورًا للناس اللي تختارهم — في كل العملاء.",
  loading: "جاري تحميل الإعلانات...",
  error: "حصل خطأ",
  success: "تمام",
  close: "قفل",
  cancel: "إلغاء",
  back: "رجوع",
  edit: "تعديل",
  delete: "مسح",
  refresh: "تحديث",
  saving: "جاري الحفظ…",
  working: "جاري التنفيذ…",
  none: "ولا حاجة",
  required: "مطلوب",

  tabs: {
    compose: "إعلان جديد",
    templates: "القوالب",
    audiences: "الفئات المحفوظة",
    history: "السجل",
  },

  stats: {
    templates: "قوالب مفعّلة",
    audiences: "فئات محفوظة",
    scheduled: "متجدولة",
    sent: "اتبعتت",
  },

  loadFailed: {
    templates: "معرفناش نحمّل القوالب",
    audiences: "معرفناش نحمّل الفئات المحفوظة",
    options: "معرفناش نحمّل اختيارات الاستهداف",
  },

  // ── labels for enums ──
  categories: {
    VERSION_UPDATE: "تحديث إصدار",
    SCHEDULED_MAINTENANCE: "صيانة متجدولة",
    UNPLANNED_DOWNTIME: "عطل مفاجئ",
    SERVICE_RESTORED: "رجوع الخدمة",
    DEGRADED_PERFORMANCE: "بطء في السيستم",
    SECURITY_NOTICE: "تنبيه أمني",
    POLICY_UPDATE: "تحديث سياسة",
    HOLIDAY_CLOSURE: "إجازة / قفل",
    PAYROLL_NOTICE: "تنبيه مرتبات",
    NEW_FEATURE: "ميزة جديدة",
    EMERGENCY: "طوارئ",
    GENERAL: "عام",
  },
  severities: { INFO: "معلومة", SUCCESS: "نجاح", WARNING: "تحذير", CRITICAL: "مهم جدًا" },
  displayModes: {
    BANNER: { label: "شريط", hint: "شريط فوق في كل الصفحات" },
    TOAST: { label: "إشعار صغير", hint: "كارت صغير في الركن وبيختفي لوحده" },
    MODAL: { label: "بوب أب", hint: "بيغطي الشاشة لحد ما يتقفل أو يتأكد" },
  },
  statuses: {
    DRAFT: "مسودة",
    SCHEDULED: "متجدول",
    SENDING: "بيتبعت",
    SENT: "اتبعت",
    CANCELLED: "اتلغى",
    FAILED: "فشل",
    EXPIRED: "خلص",
  },
  roles: { superadmin: "السوبر أدمن", admin: "الأدمن", manager: "المديرين", user: "الموظفين" },
  ruleTypes: {
    ALL: "الكل",
    ROLE: "الدور",
    CLIENT: "العميل",
    WORKING_COMPANY: "الشركة",
    DEPARTMENT: "القسم",
    ADMIN_LEVEL: "المستوى الإداري",
    GROUP: "الجروب",
    USER: "شخص معيّن",
  },
  variableTypes: { text: "نص", datetime: "تاريخ ووقت", number: "رقم" },

  // ── compose ──
  compose: {
    editing: (id) => `بتعدّل الإعلان رقم ${id}. الحفظ هيستبدله.`,
    startOver: "ابدأ من الأول",
    stepTemplate: "ابدأ من قالب",
    stepTemplateSub: "اختار رسالة جاهزة أو قالب من قوالبك",
    clear: "مسح",
    yourPresets: "قوالبك",
    stepDetails: "املا البيانات",
    stepDetailsSub: "القيم دي هتتحط مكان الـ {{placeholders}} في اللغتين",
    detailsHint: "التواريخ بتظهر بلغة كل شخص.",
    stepMessage: "الرسالة",
    stepMessageSub: "اللي الناس هتقراه وهيظهر لهم إزاي",
    titleEn: "العنوان (إنجليزي)",
    bodyEn: "الرسالة (إنجليزي)",
    titleAr: "العنوان (عربي)",
    bodyAr: "الرسالة (عربي)",
    arabicVersion: "النسخة العربي",
    arabicVersionHint: "بتظهر للي مشغلين السيستم بالعربي، ولو فاضية هيشوفوا النسخة الإنجليزي.",
    category: "النوع",
    severity: "الأهمية",
    displayAs: "طريقة الظهور",
    requireAck: "لازم يأكدوا إنهم شافوه",
    requireAckHint: "هيفضل ظاهر لحد ما كل شخص يدوس «تم الاطلاع»، وتقدر تعرف مين أكد من السجل.",
    dismissible: "يقدروا يقفلوه",
    dismissibleHint: "لو مقفولة، هيفضل ظاهر لحد ما مدته تخلص (مفيدة لإعلانات العطل اللي شغالة دلوقتي).",
    addToInbox: "ضيفه كمان في جرس الإشعارات",
    addToInboxHint: "بيفضل منه نسخة في إشعارات كل شخص.",
    stepAudience: "مين هيستلمه",
    stepAudienceSub: "حدد بالشروط، أو اختار فئة حفظتها قبل كده",
    buildRules: "حدد بالشروط",
    savedAudience: "فئة محفوظة",
    selectAudience: "اختار فئة…",
    noSavedAudiences: "مفيش فئات محفوظة لسه — اعملها من تبويب الفئات المحفوظة.",
    stepDelivery: "الإرسال",
    stepDeliverySub: "هيتبعت امتى وهيختفي امتى",
    send: "وقت الإرسال",
    now: "دلوقتي",
    schedule: "جدولة",
    date: "التاريخ",
    time: "الوقت",
    stopsShowing: "يختفي امتى",
    expiresHint: (when) => `هيختفي ${when}`,
    expiryNoneHint: "هيفضل ظاهر لحد ما يتقفل أو يتأكد، أو تسحبه إنت.",
    expiryNone: "مش هيختفي لوحده",
    expiryHours: "بعد مدة",
    expiryAt: "في وقت محدد",
    hours: "ساعة",
    errTitleEn: "العنوان الإنجليزي مطلوب",
    errBodyEn: "الرسالة الإنجليزي مطلوبة",
    errFillIn: (list) => `املا: ${list}`,
    errUnknownPlaceholders: (list) => `فيه placeholders مش معرّفة: ${list}`,
    errAudience: "اختار مين هيستلم الإعلان",
    errFutureTime: "اختار وقت إرسال لسه مجاش",
    errExpiry: "وقت الاختفاء لازم يكون بعد وقت الإرسال",
    saveAsPreset: "احفظه كقالب",
    saveDraft: "احفظ مسودة",
    sendNow: "ابعت دلوقتي",
    scheduleBtn: "جدوِله",
    preview: "المعاينة",
    previewSub: "بالظبط زي ما الناس هتشوفه",
    english: "إنجليزي",
    arabic: "عربي",
    noArabicPreview: "مفيش نص عربي — اللي مشغلين السيستم بالعربي هيشوفوا النسخة الإنجليزي.",
    recipients: (n) => `${n.toLocaleString()} مستلم`,
    sentTo: (n) => `اتبعت لـ ${n.toLocaleString()} شخص.`,
    scheduledFor: (when) => `اتجدول ${when}.`,
    draftSaved: "المسودة اتحفظت.",
    confirmSendTitle: "تبعت الإعلان ده دلوقتي؟",
    confirmScheduleTitle: "تجدول الإعلان ده؟",
    sendToN: (n) => `ابعته لـ ${n == null ? "…" : n.toLocaleString()} شخص`,
    goesOutAt: (when) => `هيتبعت ${when}`,
    goesOutNow: "هيتبعت فورًا",
    andExpires: (when) => ` · وهيختفي ${when}`,
    audience: "الجمهور",
    noArabicWarning: "مفيش نسخة عربي — اللي مشغلين السيستم بالعربي هيشوفوا الإنجليزي.",
    presetName: "اسم القالب",
    presetNameHint:
      "بيحفظ النص والمتغيرات وطريقة الظهور والمدة والفئة المحفوظة (لو فيه). القيم اللي كتبتها للمتغيرات مش بتتحفظ.",
    savePreset: "احفظ القالب",
    presetSaved: (name) => `القالب "${name}" اتحفظ.`,
  },

  // ── templates ──
  templates: {
    yourPresets: "قوالبك",
    yourPresetsSub: "رسايلك اللي بتستخدمها كتير — تقدر تعدلها في أي وقت",
    newPreset: "قالب جديد",
    noPresets: "مفيش قوالب لسه. اعمل واحد من هنا، أو دوس «احفظه كقالب» وإنت بتكتب الإعلان، أو انسخ قالب جاهز.",
    standard: "القوالب الجاهزة",
    standardSub: "جاية مع السيستم ومينفعش تتعدل. انسخ واحد عشان تعمل نسختك.",
    standardBadge: "جاهز",
    use: "استخدمه",
    duplicate: "انسخه كقالب",
    duplicateShort: "نسخ",
    colTemplate: "القالب",
    colMessage: "الرسالة",
    colStatus: "الحالة",
    colActions: "إجراءات",
    active: "مفعّل",
    off: "متوقف",
    needsAck: "محتاج تأكيد",
    hoursShort: (n) => `${n} ساعة`,
    defaultAudience: (name) => `الفئة الافتراضية: ${name}`,
    duplicated: (name) => `"${name}" اتنسخ في قوالبك.`,
    deleteTitle: "تمسح القالب؟",
    deleteBody: (name) => `"${name}" هيتمسح، والإعلانات اللي اتبعتت منه هتفضل بنصها زي ما هو.`,
    deleted: (name) => `"${name}" اتمسح.`,
  },

  // ── template editor ──
  editor: {
    editTitle: (name) => `تعديل القالب — ${name}`,
    newTitle: "قالب جديد",
    basics: "البيانات الأساسية",
    behavior: "طريقة التعامل معاه",
    name: "الاسم",
    nameHint: "إنت بس اللي بتشوفه — عشان تلاقي القالب بعدين.",
    description: "وصف (داخلي)",
    descriptionHint: "ملاحظة ليك لو حابب، مثلًا امتى تستخدم القالب ده.",
    content: "نص الإعلان",
    contentHint: "اكتب الإعلان مرة واحدة، وحط {{اسم}} في أي حتة قيمتها بتتغير كل مرة — هتملاها وقت الإرسال.",
    contentExample: "مثال: الإصدار {{version}} متاح دلوقتي",
    placeholders: "المتغيرات",
    placeholdersHint: "كل {{…}} في النص لازم يبقى ليه صف هنا، عشان اللي بيبعت يعرف يملا إيه.",
    add: "إضافة متغير",
    usedNotDefined: "مكتوبة في النص ولسه متضافتش — دوس عشان تضيفها:",
    noPlaceholders: "مفيش متغيرات — الإعلان هيتبعت زي ما هو مكتوب.",
    colKey: "المتغير",
    colLabelEn: "الاسم (إنجليزي)",
    colLabelAr: "الاسم (عربي)",
    colType: "النوع",
    previewTitle: "المعاينة",
    previewHint: "شكله والمتغيرات لسه فاضية.",
    key: "key",
    labelEn: "الاسم (إنجليزي)",
    labelAr: "الاسم (عربي)",
    removePlaceholder: "شيل المتغير",
    defaults: "الإعدادات الافتراضية",
    showForHours: "مدة الظهور (بالساعات)",
    showForHint: "فاضي = لحد ما يتقفل أو يتسحب",
    defaultAudience: "الفئة الافتراضية",
    dismissible: "يتقفل",
    savePreset: "احفظ القالب",
  },

  // ── audiences ──
  audiences: {
    title: "الفئات المحفوظة",
    subtitle: "المجموعات اللي بتبعتلها كتير — اختارها بدوسة واحدة وإنت بتكتب الإعلان",
    newAudience: "فئة جديدة",
    empty: "احفظ المجموعات اللي بتبعتلها كتير، وبعدين اختارها بدوسة واحدة وإنت بتكتب الإعلان.",
    colName: "الاسم",
    colWho: "مين",
    colActions: "إجراءات",
    editTitle: (name) => `تعديل الفئة — ${name}`,
    newTitle: "فئة جديدة",
    basics: "البيانات الأساسية",
    name: "الاسم",
    namePlaceholder: "مثلًا: كل مديرين شركة كذا",
    description: "الوصف",
    rules: "الشروط",
    rulesHint: "أي حد ينطبق عليه شرط «ضمّ»، ما عدا اللي ينطبق عليه شرط «استبعد».",
    save: "احفظ الفئة",
    saved: (name) => `الفئة "${name}" اتحفظت.`,
    deleteTitle: "تمسح الفئة؟",
    deleteBody: (name) =>
      `"${name}" هتتمسح. الإعلانات اللي اتبعتت لها مش هتتأثر — كل إعلان محتفظ بنسخة من الشروط بتاعته.`,
    deleted: (name) => `"${name}" اتمسحت.`,
  },

  // ── reach counter ──
  reach: {
    incomplete: "كمّل الشروط عشان تشوف هيوصل لمين.",
    counting: "جاري العد…",
    reaches: (n) => `هيوصل لـ ${n.toLocaleString()} شخص`,
    eg: "زي",
  },

  // ── rule builder ──
  rules: {
    include: "ضمّ",
    exclude: "استبعد",
    includeOrExclude: "ضمّ ولا استبعاد",
    ruleType: "نوع الشرط",
    none: "مفيش شروط لسه — ضيف مين يستلم.",
    addRule: "إضافة شرط",
    addExclusion: "إضافة استبعاد",
    removeRule: "شيل الشرط",
    everyActive: "كل الحسابات الشغالة",
    selectRole: "اختار الدور…",
    selectClient: "اختار العميل…",
    client: "العميل…",
    company: "الشركة…",
    selectDepartment: "اختار القسم…",
    selectAdminLevel: "اختار المستوى الإداري…",
    selectGroup: "اختار الجروب…",
    searchUser: "ابحث بالاسم أو الإيميل أو التليفون أو كود العميل…",
    noUsers: "مفيش حد مطابق",
    change: "تغيير",
    user: (id) => `مستخدم #${id}`,
    clientRule: (name) => `العميل: ${name}`,
    deptRule: (name, company) => `قسم: ${name}${company ? ` (${company})` : ""}`,
    deptN: (id) => `قسم #${id}`,
    adminLevelRule: (name) => `مستوى إداري: ${name}`,
    adminLevelN: (id) => `مستوى إداري #${id}`,
    groupRule: (name) => `جروب: ${name}`,
    group: "جروب",
    except: (label) => `ما عدا ${label}`,
    listSep: "، ",
  },

  // ── history ──
  history: {
    title: "سجل الإعلانات",
    subtitle: "كل الإعلانات بحالتها وإحصائيات وصولها",
    status: "الحالة",
    allStatuses: "كل الحالات",
    empty: "مفيش حاجة لسه. الإعلانات اللي هتبعتها أو تجدولها هتظهر هنا.",
    colAnnouncement: "الإعلان",
    colStatus: "الحالة",
    colAudience: "الجمهور",
    colWhen: "امتى",
    colDelivery: "الوصول",
    colActions: "إجراءات",
    fromTemplate: (name) => `من "${name}"`,
    scheduledAt: (when) => `متجدول ${when}`,
    sentAt: (when) => `اتبعت ${when}`,
    createdAt: (when) => `اتعمل ${when}`,
    until: (when) => `لحد ${when}`,
    by: (who) => `بواسطة ${who}`,
    recipientsN: (n) => `${n.toLocaleString()} مستلم`,
    seen: "شافوه",
    acknowledged: "أكدوا",
    recipients: "المستلمين",
    sendNow: "ابعت دلوقتي",
    cancel: "إلغاء",
    retract: "اسحبه",
    confirm: {
      send: { title: "تبعته دلوقتي؟", body: "هيتبعت فورًا لكل اللي ينطبق عليهم الجمهور.", btn: "ابعت دلوقتي" },
      cancel: { title: "تلغي الإعلان ده؟", body: "مش هيتبعت.", btn: "إلغاء الإعلان" },
      retract: {
        title: "تسحب الإعلان ده؟",
        body: "هيختفي من كل الشاشات فورًا، وإحصائيات الوصول هتفضل محفوظة.",
        btn: "اسحبه",
      },
      delete: { title: "تمسح الإعلان ده؟", body: "هيتمسح من السجل نهائي.", btn: "مسح" },
    },
    done: { send: "اتبعت.", retract: "اتسحب من شاشات الكل.", cancel: "اتلغى.", delete: "اتمسح." },
    recipientsTitle: (title) => `المستلمين — ${title}`,
    filters: { all: "الكل", unseen: "مشافوش", seen: "شافوه", acknowledged: "أكدوا", dismissed: "قفلوه" },
    colPerson: "الشخص",
    colRole: "الدور",
    colClient: "العميل",
    colSeen: "شافه",
    colAck: "أكد",
    colDismissed: "قفله",
    nobody: "مفيش حد هنا.",
    loadingRecipients: "جاري تحميل المستلمين...",
  },
};

export const text = { en, ar };
export default text;
